import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { useSelector } from 'react-redux';
import CrewUser from './components/CrewUser';
import EditIcon from 'assets/images/edit-icon.svg';
import styles from './styles';
import { compareWorkersCrewLeaderIndividualThenAlpha } from 'helpers/_helpers';
import { useArrayHistoryChanges } from 'components/WorklogDetails/hooks/useArrayHistoryChanges';
import { useGetHistoryItem } from 'components/WorklogDetails/hooks/useGetHistoryItem';
import { selectLaborClassificationName, selectLaborClassificationsState } from 'store/laborClassifications';
import { getWorkerLcOverrideNames } from 'helpers/laborClassification';
import { EntityChip } from 'lib';

const CrewBlock = ({
  classes,
  workers,
  defaultLaborClassificationId,
  getFormattedHoursRange,
  openAdditionalDrawer,
  hasAccess,
  openUserWorklogs,
  historyState,
}) => {
  const defaultLcName = useSelector((state) =>
    selectLaborClassificationName(state, defaultLaborClassificationId),
  );
  const lcState = useSelector(selectLaborClassificationsState);
  const displayWorkers = useMemo(() => workers.slice().sort(compareWorkersCrewLeaderIndividualThenAlpha), [workers]);

  const someWorkerHasLcOverride = useMemo(() => {
    const sheetLike = { defaultLaborClassificationId };
    return displayWorkers.some((worker) =>
      getWorkerLcOverrideNames(worker, sheetLike, lcState?.byId).length > 0,
    );
  }, [displayWorkers, defaultLaborClassificationId, lcState]);
  
  const { removedItems, addedItems, changedItems } = useArrayHistoryChanges(historyState?.diff?.workers);

  const historyItem = useGetHistoryItem(workers, addedItems, changedItems);
  const displayIndividualHeaders = !!workers.some((data) => data?.hours) ||
    (!!historyState && changedItems?.some(([key, value]) => !!value?.hours))

  return (
    <div>
      {defaultLaborClassificationId && defaultLcName && (
        <div className={classes.defaultLcRow}>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classnames(classes.regularFontWeight, classes.defaultLcLabel)}
          >
            Default Labor Classification:
          </Typography>
          <EntityChip label={defaultLcName} editable={false} />
        </div>
      )}
      <Grid
        container
        spacing={2}
        direction={'row'}
        wrap={'nowrap'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Grid item xs={3}>
          <Typography variant="body1" color="textSecondary" className={classes.regularFontWeight}>
            Crew:
          </Typography>
        </Grid>
        <Grid container item xs={8} className={classes.hidForMobileWrapper}>
          {displayIndividualHeaders && (
            <>
              <Grid item xs={5}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.regularFontWeight}
                >
                  On Job Time:
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.regularFontWeight}
                >
                  Individual Time To:
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.regularFontWeight}
                  style={{ paddingLeft: 15}}
                >
                  Individual Time From:
                </Typography>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item container xs={1} justifyContent={'flex-end'} spacing={0}>
          {hasAccess && (
            <IconButton
              className={classes.editButton}
              onClick={openAdditionalDrawer('resources', 'people')}
              disableRipple
              disableTouchRipple
            >
              <img src={EditIcon} alt="edit crew" />
            </IconButton>
          )}
        </Grid>
      </Grid>
      <>
        {/* {workers.sort(compareByForeman).map((w) => (epicmock */}
        {displayWorkers?.length ? displayWorkers.map((w, i) => (
          <CrewUser
            key={w._id}
            workerData={w}
            getFormattedHoursRange={getFormattedHoursRange}
            hasAccess={hasAccess}
            openUserWorklogs={openUserWorklogs(w._id, w.hours, w)}
            historyState={historyState}
            historyItem={historyItem(w)}
            reserveLcIconSpace={someWorkerHasLcOverride}
          />
        )) : null}
        {(historyState && removedItems?.length) ? removedItems.map((w) => (
          <CrewUser
            key={w._id}
            workerData={w}
            getFormattedHoursRange={getFormattedHoursRange}
            hasAccess={false}
            openUserWorklogs={() => {}}
            removed={true}
            reserveLcIconSpace={someWorkerHasLcOverride}
          />
        )) : null}
      </>
    </div>
  );
};

CrewBlock.propTypes = {
  classes: propTypes.object.isRequired,
  workers: propTypes.array.isRequired,
  defaultLaborClassificationId: propTypes.string,
  getFormattedHoursRange: propTypes.func.isRequired,
  openAdditionalDrawer: propTypes.func.isRequired,
  openUserWorklogs: propTypes.func.isRequired,
  hasAccess: propTypes.bool.isRequired,
};

CrewBlock.defaultProps = {
  defaultLaborClassificationId: null,
};

export default withStyles(styles)(CrewBlock);
